import { Component, DestroyRef, OnInit } from "@angular/core";
import { UserRegistration } from "../models/user";
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from "@angular/forms";
import {
    usernameValidators,
    passwordValidators,
    identicalPasswordsValidator,
} from "../validation";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    controlErrorMessages$,
    formErrorMessages$,
    setErrors,
    updateFormValidity,
} from "../utils";
import { Router, RouterLink } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { ToastService } from "../../services/toast.service";
import { CommonModule } from "@angular/common";

type RegisterForm = {
    [key in keyof UserRegistration]: FormControl<UserRegistration[key]>;
};

@Component({
    selector: "fgai-register",
    templateUrl: "./register.component.html",
    styleUrls: ["./register.component.scss"],
    standalone: true,
    imports: [CommonModule, RouterLink, ReactiveFormsModule],
})
export class RegisterComponent implements OnInit {
    public form = new FormGroup<RegisterForm>(
        {
            username: new FormControl<string>("", {
                nonNullable: true,
                validators: [Validators.required, ...usernameValidators],
            }),
            email: new FormControl<string>("", {
                nonNullable: true,
                validators: [Validators.required, Validators.email],
            }),
            password1: new FormControl<string>("", {
                nonNullable: true,
                validators: [Validators.required, ...passwordValidators],
            }),
            password2: new FormControl<string>("", {
                nonNullable: true,
                validators: [Validators.required, ...passwordValidators],
            }),
        },
        {
            validators: identicalPasswordsValidator<keyof RegisterForm>(
                "password1",
                "password2"
            ),
        }
    );

    public usernameErrors$ = controlErrorMessages$(this.form, "username");
    public emailErrors$ = controlErrorMessages$(this.form, "email");
    public password1Errors$ = controlErrorMessages$(
        this.form,
        "password1",
        "password"
    );
    public password2Errors$ = controlErrorMessages$(
        this.form,
        "password2",
        "password"
    );
    public formErrors$ = formErrorMessages$(this.form);

    public loading$ = this.authService.registration.loading$;

    constructor(
        private authService: AuthService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.authService.registration.error$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => setErrors(result.error, this.form));

        this.authService.registration.success$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toastService.show({
                    header: $localize`Registratie succesvol`,
                    body: $localize`Je hebt een activatielink in de mail ontvangen.`,
                    type: "success",
                });
                this.router.navigate(["/"]);
            });
    }

    public submit(): void {
        this.form.markAllAsTouched();
        updateFormValidity(this.form);
        if (!this.form.valid) {
            return;
        }
        this.authService.registration.subject.next(this.form.getRawValue());
    }
}
